import React from "react";
import { useTranslation } from "react-i18next";
import type { AdminUserRow, AdminUserSort, SortDirection } from "../../../graphql/types";
import { formatDate, formatNumberDe, formatRelativeDe } from "../../../utils/format";
import { avatarGradient, initialsOf } from "../userHelpers";
import { UserOriginBar } from "./UserOriginBar";

interface UsersTableProps {
  items: AdminUserRow[] | null;
  loading: boolean;
  sort: AdminUserSort;
  sortDir: SortDirection;
  onSort: (key: AdminUserSort) => void;
  onResetFilters: () => void;
}

/** A sortable column header cell. */
const SortHeader: React.FC<{
  label: string;
  active: boolean;
  dir: SortDirection;
  onClick: () => void;
}> = ({ label, active, dir, onClick }) => (
  <button className={`users-th users-th--sort${active ? " users-th--active" : ""}`} onClick={onClick}>
    {label}
    {active && <span className="users-sort-arrow">{dir === "ASC" ? "↑" : "↓"}</span>}
  </button>
);

/**
 * Users table (FE-5): sortable header (USER · LAST LOGIN · PROJEKTE), skeleton
 * loading rows, data rows, and an empty state. Rows are **display only** — not
 * clickable (User Detail is Post-V1).
 */
export const UsersTable: React.FC<UsersTableProps> = ({
  items,
  loading,
  sort,
  sortDir,
  onSort,
  onResetFilters,
}) => {
  const { t } = useTranslation();

  return (
    <div className="users-table">
      <div className="users-row users-row--head">
        <SortHeader
          label={t("users.col.user")}
          active={sort === "NAME"}
          dir={sortDir}
          onClick={() => onSort("NAME")}
        />
        <SortHeader
          label={t("users.col.lastLogin")}
          active={sort === "LOGIN"}
          dir={sortDir}
          onClick={() => onSort("LOGIN")}
        />
        <span className="users-th">{t("users.col.subscription")}</span>
        <span className="users-th">{t("users.col.trial")}</span>
        <SortHeader
          label={t("users.col.projects")}
          active={sort === "PROJECTS"}
          dir={sortDir}
          onClick={() => onSort("PROJECTS")}
        />
        <span className="users-th users-th--stacked">
          <span>{t("users.col.library")}</span>
          <span className="users-th-note">
            {t("users.col.libraryOwnerOnly")}
          </span>
        </span>
        <span className="users-th">{t("users.col.origin")}</span>
      </div>

      {!items && loading ? (
        <UsersSkeleton />
      ) : items && items.length > 0 ? (
        items.map((u) => <UserRow key={u.id} user={u} />)
      ) : (
        <UsersEmpty onReset={onResetFilters} />
      )}
    </div>
  );
};

const UserRow: React.FC<{ user: AdminUserRow }> = ({ user }) => {
  const { t } = useTranslation();
  const trialUrgent = user.trialDaysLeft != null && user.trialDaysLeft <= 3;

  return (
    <div className="users-row">
      {/* USER */}
      <div className="users-cell users-cell--user">
        <span className="users-avatar" style={{ backgroundImage: avatarGradient(user.id) }}>
          {initialsOf(user.name)}
        </span>
        <span className="users-user-meta">
          <span className="users-user-name">{user.name}</span>
          <span className="users-user-email">{user.email}</span>
        </span>
      </div>

      {/* LAST LOGIN */}
      <div className="users-cell users-cell--login">
        {user.lastLogin ? (
          <>
            <span className="users-login-date">{formatDate(user.lastLogin)}</span>
            <span className="users-login-rel">{formatRelativeDe(user.lastLogin)}</span>
          </>
        ) : (
          <span className="users-muted">{t("users.never")}</span>
        )}
      </div>

      {/* SUBSCRIPTION */}
      <div className="users-cell">
        <span className={`users-pill users-pill--${user.subscriptionStatus.toLowerCase()}`}>
          <span className="users-pill-dot" />
          {t(`users.sub.${user.subscriptionStatus.toLowerCase()}`)}
        </span>
      </div>

      {/* TRIAL */}
      <div className="users-cell">
        {user.inTrial && user.trialDaysLeft != null ? (
          <span className={`users-trial${trialUrgent ? " users-trial--urgent" : ""}`}>
            <span className="users-trial-dot" />
            {t("users.trialLeft", { count: user.trialDaysLeft })}
          </span>
        ) : (
          <span className="users-muted">—</span>
        )}
      </div>

      {/* PROJEKTE */}
      <div className="users-cell users-cell--projects">
        <span className="users-badge users-badge--own">
          {formatNumberDe(user.projectsOwned)} {t("users.own")}
        </span>
        <span className="users-badge users-badge--into">
          {formatNumberDe(user.projectsInto)} {t("users.into")}
        </span>
      </div>

      {/* LIBRARY */}
      <div className="users-cell users-cell--library">
        <span className="users-lib">
          <span className="users-lib-key">S</span> {formatNumberDe(user.songs)}
        </span>
        <span className="users-lib">
          <span className="users-lib-key">SL</span> {formatNumberDe(user.setlists)}
        </span>
        <span className="users-lib">
          <span className="users-lib-key">G</span> {formatNumberDe(user.gigs)}
        </span>
      </div>

      {/* HERKUNFT */}
      <div className="users-cell users-cell--origin">
        <UserOriginBar sources={user.songSources} />
      </div>
    </div>
  );
};

const UsersSkeleton: React.FC = () => (
  <>
    {[0, 1, 2, 3, 4, 5].map((i) => (
      <div className="users-row users-row--skeleton" key={i}>
        <div className="users-cell users-cell--user">
          <div className="lyra-skeleton users-skeleton-avatar" />
          <div className="users-user-meta">
            <div className="lyra-skeleton users-skeleton-line" />
            <div className="lyra-skeleton users-skeleton-line users-skeleton-line--sm" />
          </div>
        </div>
        <div className="lyra-skeleton users-skeleton-line" />
        <div className="lyra-skeleton users-skeleton-pill" />
        <div className="lyra-skeleton users-skeleton-line users-skeleton-line--sm" />
        <div className="lyra-skeleton users-skeleton-line" />
        <div className="lyra-skeleton users-skeleton-line" />
        <div className="lyra-skeleton users-skeleton-line users-skeleton-line--sm" />
      </div>
    ))}
  </>
);

const UsersEmpty: React.FC<{ onReset: () => void }> = ({ onReset }) => {
  const { t } = useTranslation();
  return (
    <div className="users-empty">
      <span className="users-empty-icon">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
        </svg>
      </span>
      <div className="users-empty-title">{t("users.empty.title")}</div>
      <p className="users-empty-body">{t("users.empty.body")}</p>
      <button className="users-empty-reset" onClick={onReset}>
        {t("users.empty.reset")}
      </button>
    </div>
  );
};
